import { __rest } from "tslib";
import React, { useState, lazy, Suspense } from 'react';
import { CloseIconSmall } from "@hm-group/fabric-icons/icons/CloseIcon";
import { InputValidIconSmall } from "@hm-group/fabric-icons/icons/InputValidIcon";
import cn from 'classnames';
import { CheckmarkerIcon, CrossIcon } from "../../Icon";
import BodyText from "../../Typography/texts/BodyText";
import ScreenReaderOnlyText from '../../../helpers/ScreenReaderOnlyText';
import BaseInput from '../BaseInput';
import Field from '../Field';
import styles from './PasswordInput.module.scss';
import { REGEXES_ORDER, getValidationResultsByRule, getIsValidPassword } from './validate';
const PasswordMeter = lazy(() => import(/* webpackChunkName: "password-meter" */ './PasswordMeter'));
const SafetyCheck = ({ name, passed, shouldUseExplicitValidation, isFCSolution = false, }) => {
    return (React.createElement("span", { "aria-live": "polite", className: cn(shouldUseExplicitValidation ? styles.safetyCheckExplicit : styles.safetyCheck, passed && styles.safetyCheckPass, isFCSolution && styles.safetyCheckFC) },
        name,
        passed && (React.createElement(React.Fragment, null,
            isFCSolution ? (React.createElement(InputValidIconSmall, { "data-testid": "checkmarkIcon", className: styles.checkIconFC })) : (React.createElement(CheckmarkerIcon, { "data-testid": "checkmarkIcon", className: styles.checkIcon })),
            React.createElement(ScreenReaderOnlyText, null, `${name} passed`))),
        !passed && shouldUseExplicitValidation && (React.createElement(React.Fragment, null,
            isFCSolution ? (React.createElement(CloseIconSmall, { "data-testid": "crossIcon", className: styles.crossIconFC })) : (React.createElement(CrossIcon, { "data-testid": "crossIcon", className: styles.crossIcon })),
            React.createElement(ScreenReaderOnlyText, null, `${name} not passed`)))));
};
const DEFAULT_LABELS = {
    enoughCharacters: '8 characters',
    enoughNumbers: '1 number',
    enoughUppercaseCharacters: '1 uppercase',
    enoughLowercaseCharacters: '1 lowercase',
    showPassword: 'show',
    hidePassword: 'hide',
    passwordStrength: 'Password strength',
    passwordStrengthMeterText: {},
};
const PasswordInput = (_a) => {
    var { value, required = false, name, hint, label, onChange, copyLabels = DEFAULT_LABELS, shouldDisableValidation, disableSecureCheck, disableLabel, error, className, shouldHideTextAfterInput, disablePasswordMeter = false, isFCSolution = false, minLength = 8 } = _a, restProps = __rest(_a, ["value", "required", "name", "hint", "label", "onChange", "copyLabels", "shouldDisableValidation", "disableSecureCheck", "disableLabel", "error", "className", "shouldHideTextAfterInput", "disablePasswordMeter", "isFCSolution", "minLength"]);
    DEFAULT_LABELS.enoughCharacters = `${minLength} characters`;
    const allChecksPassed = getIsValidPassword(value, minLength);
    const validationResults = getValidationResultsByRule(value, minLength);
    const [isFieldFocused, setIsFieldFocused] = useState(false);
    const [isFieldBlurred, setIsFieldBlurred] = useState(false);
    const renderSecureCheck = () => {
        if (disableSecureCheck) {
            return null;
        }
        return (React.createElement("div", null,
            React.createElement(Suspense, { fallback: null },
                React.createElement(PasswordMeter, { isVisible: allChecksPassed && !disablePasswordMeter && !isFCSolution, value: value, label: copyLabels.passwordStrength, text: copyLabels.passwordStrengthMeterText })),
            React.createElement("div", { className: cn(styles.safetyCheckWrapper, isFCSolution && styles.safetyCheckWrapperFC) }, REGEXES_ORDER.map((regexKey) => (React.createElement(SafetyCheck, { key: regexKey, name: copyLabels[regexKey], passed: validationResults[regexKey], shouldUseExplicitValidation: isFieldFocused || isFieldBlurred, isFCSolution: isFCSolution }))))));
    };
    const handleChange = (event) => {
        if (!onChange) {
            return;
        }
        const password = event.target.value;
        onChange(password, getIsValidPassword(password, minLength));
    };
    const [shouldShowPassword, setShouldShowPassword] = useState(false);
    const renderShowHidePasswordButton = () => {
        return (React.createElement("button", { role: "switch", "aria-checked": shouldShowPassword, "data-testid": "showHideButton", className: styles.showHidePasswordButton, type: "button", onClick: () => setShouldShowPassword(!shouldShowPassword) },
            React.createElement(BodyText, { Tag: "span", className: cn(error && styles.error) }, shouldShowPassword ? copyLabels.hidePassword : copyLabels.showPassword)));
    };
    return (React.createElement(Field, { isPassword: true, shouldDisableValidation: shouldDisableValidation, shouldShowValidationIcon: false, disableLabel: disableLabel, required: required, name: name, inputID: name, hint: hint, label: label, error: error, value: value, className: className, shouldHideTextAfterInput: !disableSecureCheck, render: ({ validationStatus }) => {
            return (React.createElement(React.Fragment, null,
                React.createElement("div", { className: styles.inputWrapper },
                    React.createElement(BaseInput, Object.assign({}, restProps, { "aria-required": required, required: required, type: shouldShowPassword ? 'text' : 'password', id: name, isInvalid: validationStatus === 'invalid', value: value, name: name, onChange: handleChange, autoComplete: "current-password", isFCSolution: isFCSolution, onFocus: () => {
                            setIsFieldFocused(true);
                        }, onBlur: (event) => {
                            setIsFieldFocused(false);
                            setIsFieldBlurred(true);
                            if (restProps === null || restProps === void 0 ? void 0 : restProps.onBlur) {
                                restProps.onBlur(event);
                            }
                        } })),
                    renderShowHidePasswordButton()),
                renderSecureCheck()));
        } }));
};
export default PasswordInput;
//# sourceMappingURL=PasswordInput.js.map